import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navbar, Container, Nav, NavDropdown, Badge, Spinner, OverlayTrigger, Tooltip, Button } from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Zap, LogOut, User, Layout, ShieldCheck, GraduationCap, Settings } from 'lucide-react';
import { logout, setAppContext } from '../../redux/slices/userSlice';
import api, { getActiveTasksCount } from '../../api/api';
import StudioSettingsPanel from './StudioSettingsPanel';

const WhitelabelHeader = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    // 1. Redux State Selectors
    const { user, branding, appContext } = useSelector((state) => state.user || {});
    
    // 2. Local Header States
    const [activeTaskCount, setActiveTaskCount] = useState(0);
    const [walletBalance, setWalletBalance] = useState(0);
    const [tenantName, setTenantName] = useState("");

    const slug = user?.tenant_slug || null;
    const isAdmin = user?.role === 'tenant_admin' || user?.role === 'system_admin';

    // 3. Data Sync (Wallet, Tasks, Branding)
    useEffect(() => {
        if (!slug) return;

        const fetchHeaderData = async () => {
            try {
                // Fetch Wallet & Tenant Metadata
                const walletRes = await api.get(`/tenant/${slug}/wallet/balance`);
                setWalletBalance(walletRes.data.balance || 0);
                setTenantName(walletRes.data.tenant_name);

                // Fetch Global Tasks if Admin (Studio Context Only)
                if (isAdmin && appContext === 'studio') {
                    const taskRes = await getActiveTasksCount(slug);
                    setActiveTaskCount(taskRes.data.count || 0);
                }
            } catch (err) {
                console.error("Whitelabel Header sync failed", err);
            }
        };

        fetchHeaderData();
        const interval = setInterval(fetchHeaderData, 15000); 
        return () => clearInterval(interval);
    }, [isAdmin, slug, appContext]);

    const handleLogout = () => {
        dispatch(logout());
        navigate(appContext === 'classroom' ? '/classroom-login' : '/login');
    };

    const handleSwitchContext = (newContext) => {
        dispatch(setAppContext(newContext));
        if (newContext === 'classroom') {
            navigate(`/tenant/${slug}/copilot`);
        } else {
            navigate(`/tenant/${slug}/dashboard`);
        }
    };

    // isClassroom is true when:
    //   • Redux appContext is 'classroom' (set after explicit login/switch), OR
    //   • The current URL contains /copilot (tenant_admin visiting copilot directly
    //     without triggering setAppContext — the most common invisible-text scenario)
    const isClassroom = appContext === 'classroom' || location.pathname.includes('/copilot');
    const [showSettings, setShowSettings] = useState(false);

    // Apply the studio user's own saved theme on every location change.
    // This prevents a student's AskOS theme (stored on <html data-askos-theme>)
    // from bleeding into the faculty/studio session.
    useEffect(() => {
        const saved = (() => {
            try { return JSON.parse(localStorage.getItem('studio_prefs') || '{}'); } catch { return {}; }
        })();
        document.documentElement.setAttribute('data-askos-theme', saved.theme || 'light');
    }, [location.pathname]);

    return (
        <>
        <Navbar
            bg={isClassroom ? "dark" : "white"}
            variant={isClassroom ? "dark" : "light"}
            expand="lg"
            className={`sticky-top shadow-sm border-bottom py-2 ${isClassroom ? 'classroom-nav' : ''}`}
            style={isClassroom ? { backgroundColor: '#1a1f2e' } : {}}
        >
            <Container fluid className="px-4">
                {/* BRANDING SECTION */}
                <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
                    <img
                        src={branding?.logoUrl || "/assets/images/logo.png"}
                        alt="Logo"
                        height={isClassroom ? "50" : "65"}
                        className="me-2"
                        onError={(e) => { e.target.src = "/assets/images/logo.png"; }}
                    />
                    <div className="d-flex flex-column">
                        <span
                            className="fw-bold"
                            style={{ color: isClassroom ? '#ffffff' : 'var(--color-brand-600, #2563eb)' }}
                        >
                            {branding?.institutionName || "Academic"}
                            {!branding?.institutionName && <span style={{ color: "#B8962E" }}>OS</span>}
                        </span>
                        <small style={{ color: isClassroom ? '#a0aec0' : '#6b7280', fontSize: '0.72rem', fontWeight: 500 }}>
                            {isClassroom ? 'Classroom Portal' : 'Embracing Academic Intelligence'}
                        </small>
                    </div>
                </Navbar.Brand>

                <Navbar.Toggle aria-controls="whitelabel-navbar-nav" aria-label="Toggle navigation" />
                
                <Navbar.Collapse id="whitelabel-navbar-nav">
                    <Nav className="me-auto ms-lg-4 align-items-center">
                        {/* 1. Resources (Studio Only) */}
                        {!isClassroom && (
                            <NavDropdown title="Resources" id="resources-dropdown">
                                <NavDropdown.Item as={Link} to="/user-guide">User Guide</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/faq">FAQ</NavDropdown.Item>
                                <NavDropdown.Divider />
                                <NavDropdown.Item as={Link} to="/best-practices">Best Practices</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/release-notes">Release Notes</NavDropdown.Item>
                            </NavDropdown>
                        )}

                        {/* 2. Frameworks (Studio Only) */}
                        {!isClassroom && (
                            <NavDropdown title="Frameworks" id="frameworks-dropdown">
                                <NavDropdown.Item as={Link} to="/content-standards">Content Standards</NavDropdown.Item>
                                <NavDropdown.Item as={Link} to="/taxonomy-standards">Taxonomy Standards</NavDropdown.Item>
                            </NavDropdown>
                        )}

                        {/* 3. Role/Context Switcher for Admins */}
                        {isAdmin && (
                            <NavDropdown 
                                title={<span className={isClassroom ? "text-warning fw-bold" : "text-primary fw-bold"}>🔄 Switch View</span>} 
                                id="context-switcher"
                                className={`ms-lg-2 px-2 border rounded ${isClassroom ? 'bg-secondary text-white border-warning' : 'bg-light'}`}
                            >
                                <NavDropdown.Header>Navigation Hub</NavDropdown.Header>
                                <NavDropdown.Item onClick={() => handleSwitchContext('studio')}>
                                    <Layout size={14} className="me-2" /> SME Production (Studio)
                                </NavDropdown.Item>
                                <NavDropdown.Item onClick={() => handleSwitchContext('classroom')}>
                                    <GraduationCap size={14} className="me-2" /> Teaching Portal (Classroom)
                                </NavDropdown.Item>
                            </NavDropdown>
                        )}
                    </Nav>

                    <Nav className="align-items-center gap-3">
                        {/* Global Task Observer (Studio Only) */}
                        {!isClassroom && isAdmin && activeTaskCount > 0 && (
                            <OverlayTrigger
                                placement="bottom"
                                overlay={<Tooltip>Active AI Tasks across all Projects</Tooltip>}
                            >
                                <Badge pill bg="warning" text="dark" className="d-flex align-items-center gap-2 px-3 py-2 border">
                                    <Spinner animation="grow" size="sm" variant="dark" aria-hidden="true" />
                                    <span className="fw-bold">{activeTaskCount} Tasks</span>
                                </Badge>
                            </OverlayTrigger>
                        )}

                        {/* Wallet (Studio Context Primary) */}
                        {!isClassroom && (
                            <Badge bg="success" className="p-2 fw-medium border shadow-sm">
                                Wallet: {walletBalance.toFixed(2)} Credits
                            </Badge>
                        )}

                        {/* User Profile Info */}
                        <div
                            className="text-end d-none d-lg-block me-2 border-start ps-3"
                            style={{ borderColor: isClassroom ? '#4a5568' : '#e5e7eb' }}
                        >
                            <small style={{ color: isClassroom ? '#a0aec0' : '#6b7280', display: 'block', fontSize: '0.7rem' }}>
                                {isClassroom ? "Faculty Account" : "Logged in as"}
                            </small>
                            <span
                                className="fw-bold"
                                style={{ fontSize: '0.85rem', color: isClassroom ? '#e2e8f0' : '#1f2937' }}
                            >
                                {user?.email}
                            </span>
                        </div>

                        {/* Settings button — same gear for all contexts */}
                        <Button
                            variant={isClassroom ? "outline-secondary" : "outline-secondary"}
                            size="sm"
                            className="rounded-circle p-2"
                            style={{ width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                     borderColor: isClassroom ? '#4a5568' : '#d1d5db',
                                     color: isClassroom ? '#e2e8f0' : '#374151' }}
                            onClick={() => setShowSettings(true)}
                            aria-label="Open settings"
                            title="Settings"
                        >
                            <Settings size={14} aria-hidden="true" />
                        </Button>

                        <Button
                            size="sm"
                            className="rounded-pill px-3 d-flex align-items-center gap-2"
                            style={{
                                background: isClassroom ? 'transparent' : 'transparent',
                                borderColor: isClassroom ? '#e53e3e' : '#dc2626',
                                color:       isClassroom ? '#fc8181' : '#dc2626',
                            }}
                            variant="outline-danger"
                            onClick={handleLogout}
                            aria-label="Log out"
                        >
                            <LogOut size={14} aria-hidden="true" /> Logout
                        </Button>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>

        {/* Settings panel — shared across studio and classroom */}
        <StudioSettingsPanel
            show={showSettings}
            onHide={() => setShowSettings(false)}
            isClassroom={isClassroom}
            userEmail={user?.email}
        />
        </>
    );
};

export default WhitelabelHeader;